import { useCallback, useEffect, useRef, useState } from 'react'
import { getShopSidebarLocked, setShopSidebarLocked } from 'storage/appStorage'

const HOVER_DELAY_MS = 100

export interface CollapsibleSidebarState {
  isLocked: boolean
  isHovered: boolean
  isExpanded: boolean
  toggleLock: () => void
  setIsHovered: (hovered: boolean) => void
  suppressHover: () => void
  releaseHover: () => void
}

export const useCollapsibleSidebar = (): CollapsibleSidebarState => {
  const [isLocked, setIsLocked] = useState<boolean>(getShopSidebarLocked)
  const [isHovered, setIsHovered] = useState<boolean>(false)

  // Use refs for the timeout and suppression flag so that updates made during
  // the same synchronous event burst (e.g. root `mouseenter` immediately
  // followed by a child `mouseenter` that wants to cancel it) see the latest
  // values instead of React's stale state snapshot.
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isSuppressedRef = useRef<boolean>(false)

  const isExpanded = isLocked || isHovered

  const clearHoverTimeout = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }, [])

  const setIsHoveredWithDelay = useCallback(
    (hovered: boolean) => {
      clearHoverTimeout()

      if (hovered) {
        if (isSuppressedRef.current) return
        hoverTimeoutRef.current = setTimeout(() => {
          hoverTimeoutRef.current = null
          if (isSuppressedRef.current) return
          setIsHovered(true)
        }, HOVER_DELAY_MS)
      } else {
        setIsHovered(false)
      }
    },
    [clearHoverTimeout]
  )

  const suppressHover = useCallback(() => {
    isSuppressedRef.current = true
    clearHoverTimeout()
    setIsHovered(false)
  }, [clearHoverTimeout])

  const releaseHover = useCallback(() => {
    isSuppressedRef.current = false
  }, [])

  const toggleLock = useCallback(() => {
    setIsLocked((prevLocked) => {
      const newLocked = !prevLocked
      setShopSidebarLocked(newLocked)
      return newLocked
    })
  }, [])

  useEffect(() => {
    return () => {
      clearHoverTimeout()
    }
  }, [clearHoverTimeout])

  return {
    isLocked,
    isHovered,
    isExpanded,
    toggleLock,
    setIsHovered: setIsHoveredWithDelay,
    suppressHover,
    releaseHover,
  }
}
